import { api } from './api'

export type DayStatus = 'done' | 'partial' | 'missed' | 'idle'
/**
 * onscreen  在手机上学 —— 切到别的 App 就停止计时
 * offscreen 离屏学习   —— 看纸质书、手机扣着，锁屏也继续计时
 *
 * ⚠️ 浏览器**无法区分**「锁屏」与「切到别的 App」：两者都只触发同一个
 * visibilitychange，没有任何 API 能分辨。所以由用户开始时声明意图 ——
 * 这比任何启发式都可靠，也比猜错了扣掉他的学习时间强。
 */
export type StudyMode = 'onscreen' | 'offscreen'

export interface CalDay { day: string; minutes: number; targetMinutes: number; status: DayStatus }
export interface Milestone {
  day: string; kind: 'exam' | 'mock' | 'checkpoint'; title: string
  subjectId?: number | null; daysLeft?: number
  targetScore?: string | null; targetStretch?: string | null
}
export interface TodayState {
  day: string; minutes: number; targetMinutes: number; targetSource: 'plan' | 'default'
  status: DayStatus
  streak: number
  baseMinutes: number
  debtMinutes: number
  bonusMinutes: number
  plan: { name: string; kind: string; minutes: number }[]
  quote: { text: string; mood: string }
  open: {
    id: number; subjectId: number | null; startedAt: string
    activeSeconds: number; paused: boolean; mode: StudyMode
  } | null
}

export interface Task {
  id: number; day: string; due_day: string | null; title: string
  note: string | null; url: string | null; subject: string | null
  daysLeft: number | null; urgent?: boolean
}

export const studyApi = {
  tasks: () => api.get<{ day: string; open: Task[]; upcoming: Task[]; done: Task[] }>('/api/study/tasks'),
  markTask: (id: number, done: boolean) =>
    api.post<{ ok: true; done: boolean; open: number }>(`/api/study/task/${id}/done`, { done }),
  today: () => api.get<TodayState>('/api/study/today'),
  calendar: (month: string) => api.get<{ month: string; days: CalDay[]; milestones: Milestone[] }>(
    `/api/study/calendar?month=${month}`),
  upcoming: (limit = 3) => api.get<{ today: string; milestones: Milestone[] }>(
    `/api/study/upcoming?limit=${limit}`),
  start: (mode: StudyMode, subjectId?: number) => api.post<{ id: number; mode: StudyMode }>(
    '/api/study/start', { mode, ...(subjectId ? { subject_id: subjectId } : {}) }),
  beat: (id: number, activeSeconds: number, paused: boolean) =>
    api.post<{ activeSeconds: number }>('/api/study/beat', { id, active_seconds: activeSeconds, paused }),
  end: (id: number, activeSeconds: number, note?: string) =>
    api.post<{ minutes: number; targetMinutes: number; status: DayStatus }>(
      '/api/study/end', { id, active_seconds: activeSeconds, note }),
}

export const fmtDuration = (secs: number) => {
  const m = Math.floor(secs / 60), s = secs % 60
  return m >= 60 ? `${Math.floor(m / 60)} 小时 ${m % 60} 分` : `${m} 分 ${String(s).padStart(2, '0')} 秒`
}

/** 计时器用的紧凑时钟：03:05 / 1:02:30。中文「x 分 xx 秒」太宽，塞不进圆环 */
export const fmtClock = (secs: number) => {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60
  const mm = String(m).padStart(2, '0'), ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}
